import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import { AxiosError } from 'axios';
import { SnackBarContext } from '../../../../sharedModules/form/contexts/SnackBarContext';
import { buildEmailPreviewHtml } from '../../../shared/buildEmailPreviewHtml';
import { StatementScheduledEmail } from '../model';
import { attachmentsWithheld, relativeTimeUntil, scheduleStatusChip } from '../utils';
import { useRenderStatementEmailBody } from '../hooks/useRenderStatementEmailBody';
import EmailBodyEditor from '../../../scheduledEmails/components/EmailBodyEditor';

interface Props {
  email: StatementScheduledEmail | null;
  sendingDisabled: boolean;
  locationAttachInvoices: boolean;
  onClose: () => void;
  onSendNow: (email: StatementScheduledEmail, overrideBody?: string) => void;
  onResumeAutomatic: (email: StatementScheduledEmail) => void;
}

const sendButtonContent = (email: StatementScheduledEmail): { label: string; tooltip?: string } => {
  if (email.schedule_status === 'manual_locked') {
    return { label: `Available in ${relativeTimeUntil(email.manual_send_locked_until)}`, tooltip: 'Manual send recently used.' };
  }
  if (email.schedule_status === 'no_recipients') {
    return { label: 'No recipients', tooltip: 'No email addresses for this account' };
  }
  return { label: 'Send Now' };
};

const StatementScheduledEmailModal: React.FC<Props> = ({
  email,
  sendingDisabled,
  locationAttachInvoices,
  onClose,
  onSendNow,
  onResumeAutomatic,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTemplateBody, setEditedTemplateBody] = useState<string | null>(null);
  const [editedRenderedBody, setEditedRenderedBody] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState<string>('');
  const [draftDirty, setDraftDirty] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const { renderEmailBody, isLoading: isRendering } = useRenderStatementEmailBody();
  const { showErrorSnackBar } = useContext(SnackBarContext);

  useEffect(() => {
    if (email) {
      setIsEditing(false);
      setEditedTemplateBody(null);
      setEditedRenderedBody(null);
      setDraftBody(email.template_body ?? email.body ?? '');
      setDraftDirty(false);
      setWarning(null);
      setConfirmClose(false);
    }
  }, [email]);

  if (!email) return null;

  const previewBody = editedRenderedBody ?? email.body;

  const startEditing = () => {
    setDraftBody(editedTemplateBody ?? email.template_body ?? email.body ?? '');
    setDraftDirty(false);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraftDirty(false);
  };

  const finishEditing = async () => {
    try {
      const response = await renderEmailBody({
        location: email.location,
        bill_code: email.bill_code,
        template_body: draftBody,
      });
      setEditedTemplateBody(draftBody);
      setEditedRenderedBody(response.data.rendered_body);
      setWarning(response.data.warning ?? null);
      setIsEditing(false);
      setDraftDirty(false);
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string }>;
      const message = axiosErr.response?.data?.error || 'Failed to render preview. Please try again.';
      showErrorSnackBar?.(message);
    }
  };

  const resetToOriginal = () => {
    setEditedTemplateBody(null);
    setEditedRenderedBody(null);
    setWarning(null);
  };

  const requestClose = () => {
    const hasUnsavedDraft = isEditing && draftDirty;
    const hasAppliedEdits = editedTemplateBody !== null;
    if (hasUnsavedDraft || hasAppliedEdits) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleEditorChange = (html: string) => {
    setDraftBody(html);
    setDraftDirty(true);
  };

  const sendButton = (() => {
    const { label, tooltip } = sendButtonContent(email);
    const effectiveTooltip = sendingDisabled ? 'Statement sending is disabled for this location.' : tooltip;
    const btn = (
      <Button
        variant="contained"
        color="primary"
        startIcon={<SendIcon />}
        onClick={() => onSendNow(email, editedTemplateBody ?? undefined)}
        disabled={!email.can_send_manually || sendingDisabled}
      >
        {label}
      </Button>
    );
    return effectiveTooltip ? (
      <Tooltip title={effectiveTooltip} placement="top" arrow>
        <span>{btn}</span>
      </Tooltip>
    ) : (
      btn
    );
  })();

  return (
    <>
      <Dialog open={!!email} onClose={requestClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">{email.company}</Typography>
            {(() => {
              const status = scheduleStatusChip(email);
              return (
                <Tooltip title={status.tooltip} placement="top" arrow>
                  <Chip label={status.label} color={status.color} size="small" variant="outlined" />
                </Tooltip>
              );
            })()}
          </Box>
          <IconButton onClick={requestClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Subject:</strong> {email.subject}
            </Typography>
            {email.next_send_at && (
              <Typography variant="body2" color="textSecondary">
                <strong>Next automatic send:</strong> {email.next_send_at}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Recipients:</strong>
              </Typography>
              {email.to_emails && email.to_emails.length > 0 ? (
                email.to_emails.map((addr) => <Chip key={addr} label={addr} size="small" />)
              ) : (
                <Chip label="No email addresses" size="small" color="error" variant="outlined" />
              )}
            </Box>
            {!locationAttachInvoices && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="warning" variant="outlined">
                  Invoice PDF attachments are disabled for this location. Enable them under Statements → Configuration.
                </Alert>
              </Box>
            )}
            {locationAttachInvoices && email.attach_invoices === false && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="warning" variant="outlined">
                  Invoice PDF attachments are disabled for this client. Enable them on the Statement List.
                </Alert>
              </Box>
            )}
            {locationAttachInvoices &&
              email.attach_invoices !== false &&
              email.debit_invoice_count > 0 &&
              attachmentsWithheld(email) && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="warning" variant="outlined">
                  Attachments withheld — Upload the missing PDFs to include attachments ({email.matched_pdf_count} of{' '}
                  {email.debit_invoice_count}).
                </Alert>
              </Box>
            )}
            {locationAttachInvoices &&
              email.attach_invoices !== false &&
              email.debit_invoice_count > 0 &&
              !attachmentsWithheld(email) &&
              email.attachment_metadata.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Attachments:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {email.attachment_metadata.map((a) => (
                      <Chip key={a.edoc_invoice_import_id} label={a.filename} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
            )}
          </Box>

          {isEditing ? (
            <EmailBodyEditor initialContent={draftBody} onChange={handleEditorChange} />
          ) : (
            <>
              <iframe
                srcDoc={buildEmailPreviewHtml(previewBody, email.sender_display_name, email.reply_to_email)}
                sandbox=""
                title="Email preview"
                style={{ width: '100%', height: 400, border: '1px solid #e0e0e0', borderRadius: 4 }}
              />
              {warning === 'invoice_table_not_matched' && (
                <Box sx={{ mt: 1, color: 'warning.main', fontSize: 13 }}>
                  Heads up: the {'{{invoice_table}}'} marker in your edits couldn&apos;t be matched, so the invoice
                  table won&apos;t be included. Re-check the editor; the marker may have been split by formatting.
                </Box>
              )}
              {editedTemplateBody !== null && (
                <Box sx={{ mt: 1 }}>
                  <Button size="small" startIcon={<RestartAltIcon />} onClick={resetToOriginal}>
                    Reset to original template
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {isEditing ? (
            <>
              <Button onClick={cancelEditing}>Cancel</Button>
              <Button variant="contained" color="primary" onClick={finishEditing} disabled={isRendering}>
                {isRendering ? <CircularProgress size={16} /> : 'Finish'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={requestClose}>Close</Button>
              <Button startIcon={<EditIcon />} onClick={startEditing}>
                Edit body
              </Button>
              {(email.schedule_status === 'auto_skipped' || email.schedule_status === 'auto_paused') && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() => onResumeAutomatic(email)}
                >
                  Resume automatic
                </Button>
              )}
              {sendButton}
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard your edits?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this email. Close the preview and discard them?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmClose(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StatementScheduledEmailModal;
